import { useState, FormEvent, useEffect, useRef } from 'react';
import { Anchor } from 'lucide-react';
import { login, mfaChallenge, setToken } from '../services/api';
import PasswordInput from '../components/PasswordInput';

import type { SaUser } from '../services/api';
interface Props { onLogin: (user: SaUser) => void; }

export default function LoginPage({ onLogin }: Props) {
  const [step, setStep]         = useState<'login' | 'mfa'>('login');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode]   = useState('');
  const [trustIp, setTrustIp]   = useState(true);
  const [error, setError]       = useState('');
  const [busy, setBusy]         = useState(false);
  const mfaInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === 'mfa') {
      setTimeout(() => mfaInputRef.current?.focus(), 50);
    }
  }, [step]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const data = await login(email, password);
      if ('mfa_required' in data && data.mfa_required) {
        setMfaToken(data.mfa_token);
        setMfaCode('');
        setStep('mfa');
      } else if ('access_token' in data) {
        setToken(data.access_token);
        localStorage.setItem('sa_user', JSON.stringify(data.user));
        onLogin(data.user);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ログインに失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function handleMfaSubmit(e?: FormEvent) {
    if (e) e.preventDefault();
    if (mfaCode.length !== 6) return;
    setBusy(true); setError('');
    try {
      const data = await mfaChallenge(mfaToken, mfaCode, trustIp);
      setToken(data.access_token);
      localStorage.setItem('sa_user', JSON.stringify(data.user));
      onLogin(data.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : '認証に失敗しました。');
      setMfaCode('');
      mfaInputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  function handleMfaCodeChange(value: string) {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setMfaCode(digits);
    if (digits.length === 6) {
      // auto-submit when 6 digits entered
      setTimeout(() => handleMfaSubmit(), 0);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', 'Hiragino Sans', sans-serif" }}>
      <div style={{ width: 400 }}>
        {/* ブランド */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, background: '#1d4ed8', borderRadius: 14, marginBottom: 14 }}>
            <Anchor size={24} color="#fff" strokeWidth={2} />
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>CATMS</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4, fontWeight: 500 }}>運営コンソール</div>
        </div>

        {/* カード */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '32px 32px 28px', border: '1px solid #e2e8f0', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          {step === 'login' && (
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>メールアドレス</label>
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="admin@example.com" required autoFocus
                  style={inputStyle}
                  onFocus={e => (e.currentTarget.style.borderColor = '#1d4ed8')}
                  onBlur={e  => (e.currentTarget.style.borderColor = '#d1d5db')}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>パスワード</label>
                <PasswordInput
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="パスワードを入力" required
                  style={inputStyle}
                  onFocus={e => (e.currentTarget.style.borderColor = '#1d4ed8')}
                  onBlur={e  => (e.currentTarget.style.borderColor = '#d1d5db')}
                />
              </div>

              {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626' }}>
                  {error}
                </div>
              )}

              <button type="submit" disabled={busy} style={{
                width: '100%', padding: '11px', background: busy ? '#93c5fd' : '#1d4ed8', color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer',
                letterSpacing: '0.01em', transition: 'background 0.15s',
              }}>
                {busy ? 'ログイン中...' : 'ログイン'}
              </button>
            </form>
          )}

          {step === 'mfa' && (
            <form onSubmit={handleMfaSubmit}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>二段階認証</div>
                <div style={{ fontSize: 13, color: '#64748b' }}>認証アプリの6桁コードを入力してください</div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>確認コード</label>
                <input
                  ref={mfaInputRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={mfaCode}
                  onChange={e => handleMfaCodeChange(e.target.value)}
                  placeholder="000000"
                  style={{ ...inputStyle, fontSize: 22, letterSpacing: '0.3em', textAlign: 'center' }}
                  onFocus={e => (e.currentTarget.style.borderColor = '#1d4ed8')}
                  onBlur={e  => (e.currentTarget.style.borderColor = '#d1d5db')}
                />
              </div>

              <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  id="trustIp"
                  checked={trustIp}
                  onChange={e => setTrustIp(e.target.checked)}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <label htmlFor="trustIp" style={{ fontSize: 13, color: '#374151', cursor: 'pointer' }}>
                  このIPアドレスを7日間信頼する
                </label>
              </div>

              {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626' }}>
                  {error}
                </div>
              )}

              <button type="submit" disabled={busy || mfaCode.length !== 6} style={{
                width: '100%', padding: '11px', background: (busy || mfaCode.length !== 6) ? '#93c5fd' : '#1d4ed8', color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: (busy || mfaCode.length !== 6) ? 'not-allowed' : 'pointer',
                letterSpacing: '0.01em', transition: 'background 0.15s', marginBottom: 12,
              }}>
                {busy ? '確認中...' : '確認'}
              </button>

              <div style={{ textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => { setStep('login'); setError(''); setMfaCode(''); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#1d4ed8', textDecoration: 'underline' }}
                >
                  ← ログイン画面に戻る
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, letterSpacing: '0.01em' };
const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', color: '#0f172a', transition: 'border-color 0.15s' };
